// src/pages/Register/Register.jsx
import { useMemo, useState, useId } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import i18n from "@/i18n";
import api from "@/utils/axios";

import cls from "./Register.module.scss";

import LogoS02 from "@/assets/NEW/logo/S02.webp";

import MrM from "@/assets/UI/login/MrM.webp";
import MrsM from "@/assets/UI/login/MrsM.webp";
import MrD from "@/assets/UI/login/MrD.webp";
import MrsD from "@/assets/UI/login/MrsD.webp";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const usernameRe = /^(?=.{3,30}$)[a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])$/;
const MIN_PWD = 8;

const is21Plus = (y, m, d) => {
  if (!y || !m || !d) return false;
  const birth = new Date(+y, +m - 1, +d);
  const now = new Date();
  const cut = new Date(now.getFullYear() - 21, now.getMonth(), now.getDate());
  return birth <= cut;
};

const bgFloatA = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.5, 0.68, 0.5],
    scale: [1.03, 1.08, 1.03],
    x: [0, 8, 0],
    transition: { duration: 13, ease: "easeInOut", repeat: Infinity },
  },
};

const bgFloatB = {
  hidden: { opacity: 0.78 },
  show: {
    opacity: [0.78, 0.62, 0.78],
    scale: [1.08, 1.03, 1.08],
    x: [0, -10, 0],
    transition: { duration: 15, ease: "easeInOut", repeat: Infinity },
  },
};

function RegisterForm({
  f,
  setField,
  errs,
  months,
  days,
  years,
  showPwd,
  setShowPwd,
  showPwd2,
  setShowPwd2,
  loading,
  onSubmit,
}) {
  const uid = useId();

  const idEmail = `${uid}-email`;
  const idUsername = `${uid}-username`;
  const idPwd = `${uid}-password`;
  const idPwd2 = `${uid}-password2`;

  return (
    <form className={cls.form} noValidate onSubmit={onSubmit}>
      <div className={cls.twoGrid}>
        <div className={cls.group}>
          <label className={cls.label} htmlFor={idEmail}>
            {i18n.t("register.emailLabel")}
          </label>

          <input
            id={idEmail}
            name="email"
            className={cls.input}
            type="email"
            value={f.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder={i18n.t("register.emailPlaceholder")}
            autoComplete="email"
            required
          />

          {errs.email ? (
            <div className={cls.errorText}>{errs.email}</div>
          ) : (
            <div className={cls.help}>{i18n.t("register.emailHelp")}</div>
          )}
        </div>

        <div className={cls.group}>
          <label className={cls.label} htmlFor={idUsername}>
            {i18n.t("register.usernameLabel")}
          </label>

          <input
            id={idUsername}
            name="username"
            className={cls.input}
            type="text"
            value={f.username}
            onChange={(e) => setField("username", e.target.value)}
            placeholder={i18n.t("register.usernamePlaceholder")}
            autoComplete="username"
            required
          />

          {errs.username ? (
            <div className={cls.errorText}>{errs.username}</div>
          ) : (
            <div className={cls.help}>{i18n.t("register.usernameHelp")}</div>
          )}
        </div>
      </div>

      <div className={cls.twoGrid}>
        <div className={cls.group}>
          <label className={cls.label} htmlFor={idPwd}>
            {i18n.t("register.passwordLabel")}
          </label>

          <div className={cls.passwordRow}>
            <input
              id={idPwd}
              name="password"
              className={cls.input}
              type={showPwd ? "text" : "password"}
              value={f.password}
              onChange={(e) => setField("password", e.target.value)}
              placeholder={i18n.t("register.passwordPlaceholder")}
              autoComplete="new-password"
              required
            />

            <button
              type="button"
              className={cls.eyeBtn}
              onClick={() => setShowPwd((v) => !v)}
              aria-label={i18n.t("register.showHide")}
            >
              {showPwd ? "🙈" : "👁️"}
            </button>
          </div>

          {errs.password && (
            <div className={cls.errorText}>{errs.password}</div>
          )}
        </div>

        <div className={cls.group}>
          <label className={cls.label} htmlFor={idPwd2}>
            {i18n.t("register.confirmPasswordLabel")}
          </label>

          <div className={cls.passwordRow}>
            <input
              id={idPwd2}
              name="password2"
              className={cls.input}
              type={showPwd2 ? "text" : "password"}
              value={f.password2}
              onChange={(e) => setField("password2", e.target.value)}
              placeholder={i18n.t("register.confirmPasswordPlaceholder")}
              autoComplete="new-password"
              required
            />

            <button
              type="button"
              className={cls.eyeBtn}
              onClick={() => setShowPwd2((v) => !v)}
              aria-label={i18n.t("register.showHide")}
            >
              {showPwd2 ? "🙈" : "👁️"}
            </button>
          </div>

          {errs.password2 && (
            <div className={cls.errorText}>{errs.password2}</div>
          )}
        </div>
      </div>

      <div className={cls.group}>
        <label className={cls.label}>{i18n.t("register.birthdayLabel")}</label>

        <div className={cls.dobRow}>
          <select
            className={cls.select}
            value={f.dobMonth}
            onChange={(e) => setField("dobMonth", e.target.value)}
            aria-label={i18n.t("register.month")}
          >
            <option value="">{i18n.t("register.month")}</option>
            {months.map((m) => (
              <option key={m.v} value={m.v}>
                {m.t}
              </option>
            ))}
          </select>

          <select
            className={cls.select}
            value={f.dobDay}
            onChange={(e) => setField("dobDay", e.target.value)}
            aria-label={i18n.t("register.day")}
          >
            <option value="">{i18n.t("register.day")}</option>
            {days.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>

          <select
            className={cls.select}
            value={f.dobYear}
            onChange={(e) => setField("dobYear", e.target.value)}
            aria-label={i18n.t("register.year")}
          >
            <option value="">{i18n.t("register.year")}</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {errs.birthday ? (
          <div className={cls.errorText}>{errs.birthday}</div>
        ) : (
          <div className={cls.help}>{i18n.t("register.birthdayHelp")}</div>
        )}
      </div>

      <div className={cls.group}>
        <label className={cls.termsRow}>
          <input
            type="checkbox"
            checked={f.agree}
            onChange={(e) => setField("agree", e.target.checked)}
          />

          <span>
            {i18n.t("register.agreePrefix")}{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              {i18n.t("register.condition4")}
            </a>
            .
          </span>
        </label>

        {errs.agree && <div className={cls.errorText}>{errs.agree}</div>}
      </div>

      <button
        type="submit"
        className={cls.submit}
        disabled={loading}
        aria-busy={loading ? "true" : "false"}
      >
        {loading ? i18n.t("register.creating") : i18n.t("register.registerBtn")}
      </button>

      <div className={cls.altRow}>
        <span>
          {i18n.t("register.alreadyHave", {
            defaultValue: "Already a citizen?",
          })}
        </span>

        <Link to="/login" className={cls.link}>
          {i18n.t("register.loginLink", { defaultValue: "Login" })}
        </Link>
      </div>
    </form>
  );
}

export default function Register() {
  const t = (k, o) => i18n.t(k, o);
  const nav = useNavigate();

  const lang = useMemo(() => {
    const v = String(i18n.language || "en").toLowerCase();
    if (v.startsWith("si")) return "si";
    if (v.startsWith("ta")) return "ta";
    if (v.startsWith("hi")) return "hi";
    return "en";
  }, []);

  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [errs, setErrs] = useState({});

  const [f, setF] = useState({
    email: "",
    username: "",
    password: "",
    password2: "",
    dobMonth: "",
    dobDay: "",
    dobYear: "",
    agree: false,
  });

  const months = [
    { v: "01", t: t("register.months.01") },
    { v: "02", t: t("register.months.02") },
    { v: "03", t: t("register.months.03") },
    { v: "04", t: t("register.months.04") },
    { v: "05", t: t("register.months.05") },
    { v: "06", t: t("register.months.06") },
    { v: "07", t: t("register.months.07") },
    { v: "08", t: t("register.months.08") },
    { v: "09", t: t("register.months.09") },
    { v: "10", t: t("register.months.10") },
    { v: "11", t: t("register.months.11") },
    { v: "12", t: t("register.months.12") },
  ];

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 100 }, (_, i) => now - 21 - i);
  }, []);

  const days = useMemo(
    () => Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")),
    [],
  );

  const setField = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};

    if (!emailRe.test(f.email)) e.email = t("register.errors.invalidEmail");

    if (!usernameRe.test(f.username)) {
      e.username = t("register.errors.usernameRules");
    }

    if (!f.password || f.password.length < MIN_PWD) {
      e.password = t("register.errors.passwordMin", { min: MIN_PWD });
    }

    if (f.password !== f.password2) {
      e.password2 = t("register.errors.passwordMismatch");
    }

    if (!f.dobMonth || !f.dobDay || !f.dobYear) {
      e.birthday = t("register.errors.birthdayRequired");
    } else if (!is21Plus(f.dobYear, f.dobMonth, f.dobDay)) {
      e.birthday = t("register.errors.mustBe21");
    }

    if (!f.agree) e.agree = t("register.errors.mustAccept");

    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErrs({});

    if (!validate()) {
      toast.error(t("register.toasts.fixFields"));
      return;
    }

    const email = f.email.trim().toLowerCase();
    const birthday = `${f.dobYear}-${f.dobMonth}-${f.dobDay}T00:00:00.000Z`;

    setLoading(true);

    try {
      const { data } = await api.post(
        "/v1/auth/signup",
        {
          username: f.username.trim(),
          email,
          password: f.password,
          birthday,
          lang,
        },
        { skipToast: true },
      );

      if (data?.accessToken) localStorage.setItem("cm_at", data.accessToken);

      const emailVerified =
        data?.user?.emailVerified === true ||
        data?.user?.emailVerifiedAt != null;

      if (!emailVerified) {
        toast.info(
          t("register.toasts.verifySent", {
            defaultValue:
              "We just sent you a verification email. Please check your inbox.",
          }),
        );
        nav(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }

      toast.success(t("register.toasts.welcome"));
      nav("/");
    } catch (err) {
      const data = err?.response?.data || {};
      const raw = String(
        data.message || data.msg || data.errmsg || err.message || "",
      );

      const userDup = /username/i.test(raw) && /(exist|taken|dup)/i.test(raw);
      const emailDup = /email/i.test(raw) && /(exist|taken|dup)/i.test(raw);

      if (userDup || emailDup) {
        setErrs((prev) => ({
          ...prev,
          ...(userDup ? { username: t("register.errors.usernameTaken") } : {}),
          ...(emailDup ? { email: t("register.errors.emailExists") } : {}),
        }));
        return;
      }

      toast.error(raw || t("register.toasts.signupFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={cls.page}>
      <section className={cls.heroStage} aria-label="Register">
        <div className={cls.universeFx} aria-hidden="true">
          <span className={cls.orbitOne} />
          <span className={cls.orbitTwo} />
          <span className={cls.energyLine} />
          <span className={cls.particles} />
        </div>

        <div className={cls.mobileOnly} aria-hidden="true">
          <Motion.img
            className={`${cls.heroImg} ${cls.z0}`}
            src={MrM}
            alt=""
            variants={bgFloatA}
            initial="hidden"
            animate="show"
            draggable="false"
          />

          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrsM}
            alt=""
            variants={bgFloatB}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly} aria-hidden="true">
          <Motion.img
            className={`${cls.heroImg} ${cls.z0}`}
            src={MrD}
            alt=""
            variants={bgFloatA}
            initial="hidden"
            animate="show"
            draggable="false"
          />

          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrsD}
            alt=""
            variants={bgFloatB}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.darkVeil} aria-hidden="true" />

        <div className={cls.overlay}>
          <Motion.div
            className={cls.logoStack}
            initial={{ opacity: 0, y: -12, scale: 0.88 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className={cls.logoPortal}>
              <span className={cls.logoGlow} />
              <span className={cls.logoRing} />
              <img src={LogoS02} alt="Noty Universe" className={cls.logo} />
            </div>

            <div
              className={cls.districtMorph}
              aria-label="Noty Universe districts"
            >
              <div className={cls.districtNames}>
                <span>
                  <strong>N</strong>
                  <em>ova</em>
                </span>

                <span>
                  <strong>O</strong>
                  <em>racle</em>
                </span>

                <span>
                  <strong>T</strong>
                  <em>erra</em>
                </span>

                <span>
                  <strong>Y</strong>
                  <em>ara</em>
                </span>
              </div>

              <div className={cls.notyWord}>
                <span>NOTY</span>
                <small>UNIVERSE</small>
              </div>
            </div>
          </Motion.div>

          <Motion.div
            className={cls.registerCard}
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.12 }}
          >
            <div className={cls.introBox}>
              <h1>{t("register.title")}</h1>

              <small>
                {t("register.slogan", {
                  defaultValue: "Built for Free Thinkers.",
                })}
              </small>
            </div>

            <RegisterForm
              f={f}
              setField={setField}
              errs={errs}
              months={months}
              days={days}
              years={years}
              showPwd={showPwd}
              setShowPwd={setShowPwd}
              showPwd2={showPwd2}
              setShowPwd2={setShowPwd2}
              loading={loading}
              onSubmit={onSubmit}
            />
          </Motion.div>
        </div>
      </section>
    </main>
  );
}
